/**
 * Tradutor único de erro da API para a frase que a pessoa lê.
 *
 * Cada tela traduzia do seu jeito: umas mostravam `err.message` do axios
 * ("Request failed with status code 500"), outras uma frase fixa que não
 * dizia se valia tentar de novo, e a KB chegava a mostrar a página HTML de
 * 500 picada em caracteres. O backend responde `{detail, code, referencia}`;
 * aqui isso vira texto, e o que não veio do backend vira uma frase pelo
 * status.
 */

import axios from 'axios'

/** Evento que o `ServerWakeGuard` escuta para abrir o popup de servidor fora. */
export const SERVER_UNAVAILABLE_EVENT = 'optimus:server-unavailable'

export interface ApiErrorInfo {
  /** Frase pronta para a tela. */
  message: string
  status?: number
  code?: string
  /** Referência da requisição (trace id), para quem abre chamado com o TI. */
  referencia?: string
  /** Sem resposta, ou o proxy na frente do servidor respondeu que ele não está lá. */
  serverUnavailable: boolean
}

const SERVIDOR_FORA =
  'Não consegui falar com o servidor do Optimus. Ele pode estar desligado ou ' +
  'ligando; tente de novo em 1 minuto.'
const DEMOROU =
  'O servidor demorou demais para responder. Tente de novo; se o pedido for ' +
  'grande, reduza o período ou o filtro.'

const POR_STATUS: Record<number, string> = {
  400: 'O pedido tem algum dado inválido. Confira os campos e tente de novo.',
  401: 'Sua sessão expirou. Entre de novo para continuar.',
  403: 'Você não tem acesso a isso. Se precisar, peça a liberação ao TI.',
  404: 'Não encontrei o que foi pedido. Pode ter sido removido.',
  409: 'Alguém alterou isso enquanto você editava. Recarregue e tente de novo.',
  413: 'O arquivo ou o texto enviado é grande demais.',
  500: 'O Optimus teve um erro interno. Tente de novo; se continuar, avise o TI.',
  503: 'O servidor do Optimus está indisponível agora. Tente de novo em 1 minuto.',
}

/** Proxy (nginx/ALB) respondendo no lugar do Django: o app não está de pé. */
const STATUS_DE_SERVIDOR_FORA = new Set([502, 504])

type Corpo = {
  detail?: unknown
  error?: unknown
  message?: unknown
  code?: unknown
  referencia?: unknown
}

function textoDoCorpo(data: unknown): string | undefined {
  // Página HTML de erro (do Django antigo ou do proxy) não é mensagem.
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined
  const corpo = data as Corpo
  for (const campo of [corpo.detail, corpo.error, corpo.message]) {
    if (typeof campo === 'string' && campo.trim()) return campo.trim()
  }
  return undefined
}

function minutosDeEspera(retryAfter: unknown): number | undefined {
  const segundos = Number(retryAfter)
  if (!Number.isFinite(segundos) || segundos <= 0) return undefined
  return Math.max(1, Math.ceil(segundos / 60))
}

function fraseDeLimite(retryAfter: unknown): string {
  const minutos = minutosDeEspera(retryAfter)
  const espera = minutos
    ? `Espere ${minutos} minuto${minutos > 1 ? 's' : ''}`
    : 'Espere alguns minutos'
  return `Muitas tentativas em pouco tempo. ${espera} e tente de novo.`
}

export function parseApiError(err: unknown, fallback?: string): ApiErrorInfo {
  if (!axios.isAxiosError(err)) {
    const message =
      err instanceof Error && err.message
        ? err.message
        : (fallback ?? POR_STATUS[500])
    return { message, serverUnavailable: false }
  }
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return { message: DEMOROU, serverUnavailable: false }
  }
  const response = err.response
  if (!response) {
    return { message: SERVIDOR_FORA, serverUnavailable: true }
  }
  const { status, data, headers } = response
  if (STATUS_DE_SERVIDOR_FORA.has(status)) {
    return { message: SERVIDOR_FORA, status, serverUnavailable: true }
  }
  const corpo = (data && typeof data === 'object' ? data : {}) as Corpo
  const code = typeof corpo.code === 'string' ? corpo.code : undefined
  const referencia =
    typeof corpo.referencia === 'string' ? corpo.referencia : undefined
  const message =
    status === 429
      ? fraseDeLimite(headers?.['retry-after'])
      : (textoDoCorpo(data) ??
        fallback ??
        POR_STATUS[status] ??
        POR_STATUS[500])
  return { message, status, code, referencia, serverUnavailable: false }
}

/** A frase para a tela; `fallback` vale só quando o backend não mandou texto. */
export function describeApiError(err: unknown, fallback?: string): string {
  return parseApiError(err, fallback).message
}
